import React from "react";
import { LUNAR_LAMELLA, lamellaCount, GATE_DEFAULTS } from "../../lib/planner";

const BLUE = "#2563eb";

function DimH({ x1, x2, y, text, fs }) {
  const t = fs * 0.35;
  return (
    <g stroke="#0a0a0a" strokeWidth={fs / 14} fill="#0a0a0a">
      <line x1={x1} y1={y} x2={x2} y2={y} />
      <line x1={x1} y1={y - t} x2={x1} y2={y + t} /><line x1={x2} y1={y - t} x2={x2} y2={y + t} />
      <text x={(x1 + x2) / 2} y={y - fs * 0.25} fontSize={fs} textAnchor="middle" stroke="none" fontFamily="ui-monospace, monospace">{text}</text>
    </g>
  );
}

function DimV({ x, y1, y2, text, fs, right }) {
  const t = fs * 0.35;
  return (
    <g stroke="#0a0a0a" strokeWidth={fs / 14} fill="#0a0a0a">
      <line x1={x} y1={y1} x2={x} y2={y2} />
      <line x1={x - t} y1={y1} x2={x + t} y2={y1} /><line x1={x - t} y1={y2} x2={x + t} y2={y2} />
      <text transform={`translate(${right ? x + fs : x - fs * 0.3} ${(y1 + y2) / 2}) rotate(-90)`} fontSize={fs} textAnchor="middle" stroke="none" fontFamily="ui-monospace, monospace">{text}</text>
    </g>
  );
}

export function AnsichtCanvas({ plan, layout, selected, onSelect }) {
  const seg = layout.segments[0];
  const els = seg?.elements || [];
  const lam = { ...LUNAR_LAMELLA, ...(plan.werk?.lamella || {}) };
  const fenceH = Number(plan.height) || 0, postH = Number(plan.postHeight) || 0;
  const L = Math.max(seg?.length || 0, 2000);
  const hmax = Math.max(fenceH + lam.bottom + 200, postH,
    ...els.filter((e) => e.kind === "pfeiler").map((e) => e.height),
    ...els.filter((e) => e.kind === "post" && e.height).map((e) => e.height),
    ...els.filter((e) => (e.kind === "gate" || e.kind === "field") && e.height).map((e) => e.height + (e.sockel || 0) + lam.bottom),
    ...els.filter((e) => e.kind === "sockel").map((e) => e.height + fenceH + lam.bottom + 100));
  const G = 150, M = 700, TOP = 350, DIM = 900;
  const vbW = L + 2 * M, vbH = TOP + hmax + G + DIM;
  const X = (mm) => M + mm, Y = (mm) => TOP + hmax - mm;
  const fs = Math.max(48, vbW / 75);
  const nLam = lamellaCount(fenceH, lam);
  const stroke = (id) => (selected && selected === id ? BLUE : "#52525b");
  const firstField = els.find((e) => e.kind === "field") || els.find((e) => e.kind === "gate");
  const s0 = firstField?.sockel || 0, h0 = firstField?.height || fenceH;
  const dimY1 = Y(0) + G + fs * 1.6, dimY2 = dimY1 + fs * 1.7, dimY3 = dimY2 + fs * 1.7;
  const hasAlu = els.some((e) => e.kind === "post");

  return (
    <svg viewBox={`0 0 ${vbW} ${vbH}`} className="w-full bg-white border border-zinc-200" style={{ maxHeight: 480 }} data-testid="ansicht-canvas">
      <defs>
        <pattern id="wandHatch" patternUnits="userSpaceOnUse" width={fs} height={fs} patternTransform="rotate(45)">
          <line x1="0" y1="0" x2="0" y2={fs} stroke="#a1a1aa" strokeWidth={fs / 8} />
        </pattern>
      </defs>
      <rect x={0} y={Y(0)} width={vbW} height={G} fill="#e4e4e7" stroke="#71717a" strokeWidth={fs / 14} />
      {els.map((e, i) => {
        if (e.kind === "wand") {
          const x = e.from === 0 && i === 0 ? X(0) - 300 : X(e.from);
          return <rect key={i} x={x} y={Y(hmax)} width={300} height={hmax} fill="url(#wandHatch)" stroke="#71717a" strokeWidth={fs / 14} data-testid="ansicht-wand" />;
        }
        if (e.kind === "sockel") return <rect key={i} x={X(e.from)} y={Y(e.height)} width={e.to - e.from} height={e.height} fill="#d4d4d8" stroke="#71717a" strokeWidth={fs / 16} />;
        if (e.kind === "pfeiler") {
          return (
            <g key={i} onClick={() => onSelect?.(e.id)} className="cursor-pointer" data-testid={`ansicht-pfeiler-${i}`}>
              <rect x={X(e.from)} y={Y(e.height)} width={e.width} height={e.height} fill="#ececee" stroke={stroke(e.id)} strokeWidth={fs / (selected === e.id ? 6 : 12)} />
              <text x={X(e.from) + e.width / 2} y={Y(e.height) - fs * 0.4} fontSize={fs * 0.85} textAnchor="middle" fill="#3f3f46" fontFamily="ui-monospace, monospace">{`P${e.no ?? ""} · H ${e.height}`}</text>
            </g>
          );
        }
        if (e.kind === "post") {
          const ph = e.height || postH;
          return <rect key={i} x={X(e.from)} y={Y((e.sockel || 0) + ph)} width={e.to - e.from} height={ph} fill={e.profile === "gate" ? BLUE : "#27272a"} stroke={selected === e.id ? BLUE : "none"} strokeWidth={fs / 6} onClick={() => onSelect?.(e.id)} className="cursor-pointer" data-testid={e.standalone ? `ansicht-alupost-${i}` : undefined} />;
        }
        const s = e.sockel || 0, w = e.to - e.from;
        if (e.kind === "gate") {
          const gh = e.height || fenceH, gb = e.bottom ?? lam.bottom;
          return (
            <g key={i} onClick={() => onSelect?.(e.id)} className="cursor-pointer" data-testid={`ansicht-gate-${e.no}`}>
              <rect x={X(e.from)} y={Y(s + gb + gh)} width={w} height={gh} fill={selected === e.id ? "#dbeafe" : "#eff6ff"} stroke={BLUE} strokeWidth={fs / 10} strokeDasharray={`${fs / 2} ${fs / 3}`} />
              <text x={X(e.from) + w / 2} y={Y(s + gb + gh / 2)} fontSize={fs} textAnchor="middle" fill={BLUE} fontWeight="600">{`${GATE_DEFAULTS[e.type]?.short || "Tor"} ${e.width} × H ${gh}`}</text>
              <text x={X(e.from) + w / 2} y={Y(s + gb / 2) + fs * 0.3} fontSize={fs * 0.6} textAnchor="middle" fill="#71717a">{`Bodenluft ${gb}`}</text>
            </g>
          );
        }
        const fh = e.height || fenceH, nL = fh === fenceH ? nLam : lamellaCount(fh, lam);
        return (
          <g key={i} onClick={() => onSelect?.(e.id)} className="cursor-pointer" data-testid={`ansicht-field-${e.no}`}>
            {Array.from({ length: nL }).map((_, k) => (
              <rect key={k} x={X(e.from)} y={Y(s + lam.bottom + k * (lam.height + lam.gap) + lam.height)} width={w} height={lam.height} fill="#3f3f46" />
            ))}
            <rect x={X(e.from)} y={Y(s + lam.bottom + fh)} width={w} height={fh} fill="transparent" stroke={selected === e.id ? BLUE : "none"} strokeWidth={fs / 8} />
            <text x={X(e.from) + w / 2} y={Y(s + lam.bottom + fh) - fs * 0.4} fontSize={fs * 0.85} textAnchor="middle" fill="#0a0a0a" fontWeight="600">{fh === fenceH ? `ZF ${e.no}` : `ZF ${e.no} · H ${fh}`}</text>
          </g>
        );
      })}
      {els.filter((e) => e.kind === "pfeiler" || e.standalone).map((e, i) => <DimH key={`dp${i}`} x1={X(e.from)} x2={X(e.to)} y={dimY1} text={e.to - e.from} fs={fs * 0.8} />)}
      {els.filter((e) => e.span).map((e, i) => <DimH key={`df${i}`} x1={X(e.span[0])} x2={X(e.span[1])} y={dimY1} text={e.span[1] - e.span[0]} fs={fs * 0.8} />)}
      {hasAlu && els.filter((e) => (e.kind === "field" || e.kind === "gate") && (e.sub || (e.span && e.to - e.from !== e.span[1] - e.span[0]))).map((e, i) => <DimH key={`de${i}`} x1={X(e.from)} x2={X(e.to)} y={dimY2} text={e.sub ? e.width : `${e.width} (Element)`} fs={fs * 0.75} />)}
      {seg && seg.length > 0 && <DimH x1={X(0)} x2={X(seg.length)} y={hasAlu ? dimY3 : dimY2} text={`Gesamt ${seg.length}`} fs={fs * 0.85} />}
      {firstField && <DimV x={X(0) - 350} y1={Y(s0 + lam.bottom)} y2={Y(s0 + lam.bottom + h0)} text={`Zaunhöhe ${h0}`} fs={fs * 0.8} />}
      {firstField && (s0 > 0 || lam.bottom > 0) && <DimV x={X(0) - 650} y1={Y(0)} y2={Y(s0 + lam.bottom + h0)} text={`Gesamt ${s0 + lam.bottom + h0}`} fs={fs * 0.7} />}
      {firstField && lam.bottom > 0 && <DimV x={X(0) - 350} y1={Y(s0)} y2={Y(s0 + lam.bottom)} text={lam.bottom} fs={fs * 0.7} />}
      {s0 > 0 && <DimV x={X(0) - 350} y1={Y(0)} y2={Y(s0)} text={`Sockel ${s0}`} fs={fs * 0.7} />}
      {!seg?.elements?.length && <text x={vbW / 2} y={vbH / 2} fontSize={fs * 1.2} textAnchor="middle" fill="#a1a1aa">Pfeiler und Felder in der Tabelle hinzufügen</text>}
    </svg>
  );
}
